import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Send, Sparkles, Trash2, Download, FileText, Wand2, Loader2, Save, User, Home, Upload, X, Edit3, Mail, RefreshCw, IdCard, Link2, Copy, BarChart3 } from "lucide-react";

const PROPERTY_TYPES = [
  "Multifamily", "Office", "Retail", "Industrial", "Hotel",
  "Mixed-Use", "Self-Storage", "Medical Office", "Special Purpose",
];

const emptyState = () => ({
  property_info: {
    name: "", property_type: "", address: "", city: "", state: "",
    estimated_value: null, noi: null, cap_rate_pct: null, occupancy_type: null,
  },
  listing_agent: { name: "", email: "", phone: "", brokerage: "", photo_b64: null, photo_content_type: null },
  options: [],
  research_note: null,
  research_citations: [],
});

const fmtMoney = (v) => (v == null || v === "" ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const fmtPct = (v, d = 2) => (v == null || v === "" ? "—" : `${Number(v).toFixed(d)}%`);

// Safe error message extractor — FastAPI validation returns an array of objects that would crash toast otherwise.
const errMsg = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d.length && typeof d[0]?.msg === "string") return d.map((x) => x.msg).join("; ");
  return e?.message || fallback;
};

// Fetch a PDF with the auth header and open it in a new tab via blob URL.
// Direct <a href> links to protected endpoints break in Safari because it can't send the token.
async function openAuthedPdf(url, filename = "document.pdf") {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.target = "_blank";
    a.rel = "noopener";
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke after a delay so the new tab has time to render
    setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
  } catch (e) {
    toast.error(errMsg(e, "Couldn't open PDF"));
  }
}

export default function AdminLoanQuoteStudio() {
  const { user } = useAuth();
  const isClientAgent = user?.role === "client";
  const [agentProfile, setAgentProfile] = useState(null);   // last saved profile
  const [savingProfile, setSavingProfile] = useState(false);
  const [state, setState] = useState(emptyState());
  const [sessionId, setSessionId] = useState(null);
  const [editingId, setEditingId] = useState(null);  // when set, save() PATCHes existing quote
  const [messages, setMessages] = useState([{
    role: "ada",
    text: "Hi — I'm Ada. Let's build a Loan Quote. What's the property name?\n\nTip: you can also click Edit on any saved quote below to modify it.",
  }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [library, setLibrary] = useState([]);
  const chatScrollRef = useRef(null);

  const loadLibrary = () => api.get("/admin/marketing/quotes").then((r) => setLibrary(r.data || []));
  useEffect(() => { loadLibrary(); }, []);

  // Client-only: load the persisted listing-agent profile and hydrate the form once.
  // Admins fill the agent panel per quote, so we skip this for them.
  useEffect(() => {
    if (!isClientAgent) return;
    api.get("/client/quote-studio/profile").then((r) => {
      const p = r.data || {};
      setAgentProfile(p);
      // Only hydrate on the very first mount, and only if the form hasn't been touched.
      setState((s) => (s.listing_agent?.name || s.listing_agent?.email ? s : ({
        ...s,
        listing_agent: {
          name: p.name || "",
          email: p.email || "",
          phone: p.phone || "",
          brokerage: p.brokerage || "",
          photo_b64: p.photo_b64 || null,
          photo_content_type: p.photo_content_type || null,
        },
      })));
    }).catch(() => { /* profile is optional */ });
  }, [isClientAgent]);
  useEffect(() => {
    // Scroll only the chat's own container to its bottom — DO NOT scroll the whole page.
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // ---- Chat ----
  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "broker", text }]);
    setSending(true);
    try {
      const r = await api.post("/admin/marketing/quote/chat", {
        session_id: sessionId, message: text, state,
      });
      setSessionId(r.data.session_id);
      setState(r.data.state || state);
      setMessages((m) => [...m, {
        role: "ada", text: r.data.reply,
        ready_for_rates: r.data.ready_for_rates, has_agent: r.data.has_agent,
        lookup_agent: r.data.lookup_agent,
      }]);
      if (r.data.ready_for_rates && !state.options.length && !proposing) {
        // Ada just confirmed she's ready — kick off rate research automatically
        proposeOptions();
      }
      if (r.data.lookup_agent) {
        // Ada wants us to search for the listing agent's contact info
        agentLookup();
      }
    } catch (e) {
      toast.error(errMsg(e, "Ada couldn't reply"));
    } finally {
      setSending(false);
    }
  };

  // ---- Agent lookup (Perplexity) ----
  const agentLookup = async () => {
    const a = state.listing_agent || {};
    if (!a.name || a.name.trim().length < 2) {
      setMessages((m) => [...m, {
        role: "ada",
        text: "I need the agent's name before I can search — what's their full name?",
      }]);
      return;
    }
    setLookingUp(true);
    setMessages((m) => [...m, { role: "ada", text: `Searching for ${a.name}${a.brokerage ? ` at ${a.brokerage}` : ""}…` }]);
    try {
      const r = await api.post("/admin/marketing/agent-lookup", {
        name: a.name, brokerage: a.brokerage || null,
        city: state.property_info?.city || null, state: state.property_info?.state || null,
      });
      setMessages((m) => [...m, {
        role: "ada", isLookup: true, lookup: r.data,
        text: r.data.found
          ? `Found ${r.data.emails.length} email${r.data.emails.length === 1 ? "" : "s"} and ${r.data.phones.length} phone number${r.data.phones.length === 1 ? "" : "s"}. Click any to use:`
          : "I couldn't confirm a publicly-listed email for that agent — you'll need to get it from them directly.",
      }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ada", text: `Sorry, that search failed. ${errMsg(e, "")}`.trim() }]);
    } finally {
      setLookingUp(false);
    }
  };

  // ---- Propose options (Perplexity → Claude) ----
  const proposeOptions = async () => {
    setProposing(true);
    try {
      const r = await api.post("/admin/marketing/quote/propose-options", { state });
      setState((s) => ({
        ...s,
        options: r.data.options,
        research_note: r.data.research_note || null,
        research_citations: r.data.research_citations || [],
      }));
      setMessages((m) => [...m, {
        role: "ada",
        text: r.data.research_note
          ? `Pulled live market rates and drafted 3 options. Rates are indicative — edit any cell in the preview if you have a lender quote that differs.`
          : `Drafted 3 options using industry-typical ranges. Rates are estimates — edit any cell as needed.`,
      }]);
    } catch (e) {
      toast.error(errMsg(e, "Rate research failed"));
    } finally {
      setProposing(false);
    }
  };

  // ---- Live preview ----
  const refreshPreview = async () => {
    try {
      const r = await api.post("/admin/marketing/quote/preview", { state }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      // Revoke old URL to prevent leaks
      setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (e) { /* silent — preview is best-effort */ }
  };
  useEffect(() => {
    // Regenerate preview when state changes and we have enough data
    if (state.property_info.estimated_value && state.options.length > 0) {
      const t = setTimeout(refreshPreview, 400);
      return () => clearTimeout(t);
    }
  }, [state.property_info, state.options]);

  // ---- Generate & save ----
  const generateAndSave = async () => {
    setGenerating(true);
    try {
      const url = editingId
        ? `/admin/marketing/quotes/${editingId}`
        : `/admin/marketing/quote/generate`;
      const method = editingId ? "patch" : "post";
      const r = await api[method](url, {
        state, add_listing_agent_to_crm: true,
      });
      const qid = editingId || r.data.id;
      toast.success(
        editingId
          ? "Quote updated"
          : `Quote saved${r.data.contact_id ? " · Listing agent added to CRM" : ""}`
      );
      setEditingId(qid);      // switch to edit mode after first save
      loadLibrary();
      await openAuthedPdf(`/admin/marketing/quotes/${qid}/pdf`, r.data.filename || "loan-quote.pdf");
    } catch (e) {
      toast.error(errMsg(e, "Save failed"));
    } finally {
      setGenerating(false);
    }
  };

  const editQuote = async (qid) => {
    try {
      const r = await api.get(`/admin/marketing/quotes/${qid}`);
      const q = r.data;
      setEditingId(q.id);
      setSessionId(null);
      setState({
        property_info: {
          name: "", property_type: "", address: "", city: "", state: "",
          estimated_value: null, noi: null, cap_rate_pct: null, occupancy_type: null,
          ...(q.property_info || {}),
        },
        listing_agent: {
          name: "", email: "", phone: "", brokerage: "", photo_b64: null, photo_content_type: null,
          ...(q.listing_agent || {}),
        },
        options: q.options || [],
        research_note: q.research_note || null,
        research_citations: q.research_citations || [],
      });
      setMessages([{
        role: "ada",
        text: `Loaded quote for ${q.property_info?.name || q.property_info?.address || "this property"}. Edit anything you want in the panel, or tell me what to change — e.g., "bump the bank rate to 6.75%".`,
      }]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(errMsg(e, "Load failed"));
    }
  };

  const emailQuote = async (qid, agent) => {
    if (!agent?.email) {
      toast.error("Add the listing agent's email first, then Save the quote.");
      return;
    }
    if (!window.confirm(`Email this loan quote to ${agent.email}?`)) return;
    try {
      await api.post(`/admin/marketing/quotes/${qid}/email`, {});
      toast.success(`Emailed to ${agent.email}`);
      loadLibrary();
    } catch (e) {
      toast.error(errMsg(e, "Email send failed"));
    }
  };

  const startNewQuote = () => {
    // Only confirm if there's actual work in progress
    const hasWork = !!(state.property_info.name || state.property_info.address
      || state.property_info.estimated_value || state.options.length
      || messages.length > 1);
    if (hasWork && !window.confirm("Discard current quote and start a new one?")) return;
    const fresh = emptyState();
    // Client agents get their saved profile carried into the new quote so they
    // don't retype name/email/phone/brokerage/photo every time.
    if (isClientAgent && agentProfile) {
      fresh.listing_agent = {
        name: agentProfile.name || "",
        email: agentProfile.email || "",
        phone: agentProfile.phone || "",
        brokerage: agentProfile.brokerage || "",
        photo_b64: agentProfile.photo_b64 || null,
        photo_content_type: agentProfile.photo_content_type || null,
      };
    }
    setState(fresh);
    setSessionId(null);
    setEditingId(null);
    setPreviewUrl((p) => { if (p) URL.revokeObjectURL(p); return null; });
    setMessages([{
      role: "ada",
      text: "Fresh start. What's the property name?",
    }]);
  };

  // Client-only: persist the current listing-agent fields so they carry to future quotes.
  const saveAgentProfile = async () => {
    if (!isClientAgent) return;
    setSavingProfile(true);
    try {
      const a = state.listing_agent || {};
      const r = await api.put("/client/quote-studio/profile", {
        name: a.name || null,
        email: a.email || null,
        phone: a.phone || null,
        brokerage: a.brokerage || null,
        photo_b64: a.photo_b64 || null,
        photo_content_type: a.photo_content_type || null,
      });
      setAgentProfile(r.data);
      toast.success("Agent profile saved — this carries to every new quote.");
    } catch (e) {
      toast.error(errMsg(e, "Couldn't save profile"));
    } finally {
      setSavingProfile(false);
    }
  };

  // ---- Manual field edits (all fields can be edited inline in the preview panel) ----
  const setProp = (k, v) => setState((s) => ({ ...s, property_info: { ...s.property_info, [k]: v } }));
  const setAgent = (k, v) => setState((s) => ({ ...s, listing_agent: { ...s.listing_agent, [k]: v } }));
  const setOption = (i, k, v) => setState((s) => ({
    ...s, options: s.options.map((o, idx) => idx === i ? { ...o, [k]: v } : o),
  }));

  // Auto cap-rate / NOI math (client mirror of server)
  useEffect(() => {
    const p = state.property_info;
    if (!p.estimated_value) return;
    if (p.noi && !p.cap_rate_pct) {
      const cr = Number(((p.noi / p.estimated_value) * 100).toFixed(2));
      setProp("cap_rate_pct", cr);
    } else if (p.cap_rate_pct && !p.noi) {
      const noi = Math.round(p.estimated_value * p.cap_rate_pct / 100);
      setProp("noi", noi);
    }
  }, [state.property_info.estimated_value, state.property_info.noi, state.property_info.cap_rate_pct]);

  const canPropose = !!(state.property_info.estimated_value
    && (state.property_info.noi || state.property_info.cap_rate_pct));
  const canGenerate = state.options.length >= 1;
  const lastReadyMsg = [...messages].reverse().find((m) => m.role === "ada" && m.ready_for_rates);

  return (
    <div className="min-h-screen bg-[#FBF8F1] p-6" data-testid="loan-quote-studio">
      <div className="max-w-[1440px] mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Marketing</div>
            <h1 className="font-serif text-3xl font-bold">Loan Quote Studio</h1>
            <p className="text-sm text-[#6B6558] mt-1">
              Chat with Ada to build a branded Loan Quote PDF for a listing agent. Live web rates via Perplexity.
            </p>
            {editingId && (
              <div className="mt-2 inline-flex items-center gap-2 byrd-chip byrd-chip-gold" data-testid="editing-badge">
                <Edit3 size={12} /> Editing saved quote — changes overwrite on Save
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={startNewQuote} className="byrd-btn byrd-btn-dark" data-testid="lq-new">
              <RefreshCw size={14} /> Start New Quote
            </button>
          </div>
        </div>

        {/* Main split — chat + preview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Chat pane */}
          <div className="byrd-card flex flex-col h-[720px]">
            <div className="p-4 border-b border-[#E4DFD1] flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#C89434] text-white grid place-items-center">
                <Sparkles size={14} />
              </div>
              <div>
                <div className="font-serif font-bold">Ada</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558]">// Live rate lookup ready</div>
              </div>
              <button
                onClick={startNewQuote}
                className="ml-auto text-[11px] font-mono uppercase tracking-widest text-[#6B6558] hover:text-[#C89434] flex items-center gap-1"
                data-testid="lq-restart-chat"
                title="Clear the conversation and start a new quote"
              >
                <RefreshCw size={11} /> Restart
              </button>
              {lastReadyMsg && !state.options.length && (
                <button onClick={proposeOptions} disabled={proposing}
                  className="byrd-btn byrd-btn-dark text-xs"
                  data-testid="lq-propose">
                  {proposing ? (<><Loader2 size={12} className="animate-spin" /> Researching…</>) : (<><Wand2 size={12} /> Research rates &amp; propose</>)}
                </button>
              )}
            </div>
            <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "broker" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3 py-2 rounded-md text-sm whitespace-pre-wrap ${
                    m.role === "broker"
                      ? "bg-[#1A1A1A] text-white"
                      : "bg-[#F3EEE0] text-[#1A1A1A]"
                  }`}>
                    {m.text}
                    {m.ready_for_rates && !state.options.length && (
                      <button onClick={proposeOptions} disabled={proposing}
                        className="mt-2 block text-[#C89434] font-mono text-[10px] uppercase tracking-widest hover:text-[#8A6821]"
                        data-testid="lq-propose-inline">
                        {proposing ? "Researching…" : "→ Yes, research rates now"}
                      </button>
                    )}
                    {m.isLookup && m.lookup && (
                      <div className="mt-2 space-y-1">
                        {(m.lookup.emails || []).map((e) => (
                          <button key={e} onClick={() => setState((s) => ({ ...s, listing_agent: { ...s.listing_agent, email: e } }))}
                            className="block text-left text-xs px-2 py-1 rounded bg-white border border-[#E4DFD1] hover:border-[#C89434] hover:bg-[#FBF8F1] transition w-full">
                            <Mail size={10} className="inline mr-1 text-[#C89434]" /> {e}
                          </button>
                        ))}
                        {(m.lookup.phones || []).map((p) => (
                          <button key={p} onClick={() => setState((s) => ({ ...s, listing_agent: { ...s.listing_agent, phone: p } }))}
                            className="block text-left text-xs px-2 py-1 rounded bg-white border border-[#E4DFD1] hover:border-[#C89434] hover:bg-[#FBF8F1] transition w-full">
                            <User size={10} className="inline mr-1 text-[#C89434]" /> {p}
                          </button>
                        ))}
                        {m.lookup.citations?.length > 0 && (
                          <details className="text-[10px] text-[#6B6558] mt-1">
                            <summary className="cursor-pointer font-mono uppercase tracking-widest">// sources</summary>
                            <ul className="mt-1 space-y-0.5">
                              {m.lookup.citations.map((u, ci) => (
                                <li key={ci}><a href={u} target="_blank" rel="noopener noreferrer" className="underline break-all">{u}</a></li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {(sending || lookingUp) && (
                <div className="flex justify-start">
                  <div className="bg-[#F3EEE0] px-3 py-2 rounded-md text-sm text-[#6B6558]">
                    <Loader2 size={12} className="animate-spin inline mr-2" /> {lookingUp ? "Ada is searching…" : "Ada is thinking…"}
                  </div>
                </div>
              )}
            </div>
            <div className="p-3 border-t border-[#E4DFD1] flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder="Type your reply…"
                className="flex-1 px-3 py-2 border border-[#E4DFD1] rounded-md text-sm focus:outline-none focus:border-[#C89434]"
                data-testid="lq-input"
              />
              <button onClick={sendMessage} disabled={sending || !input.trim()}
                className="byrd-btn byrd-btn-dark" data-testid="lq-send">
                <Send size={14} />
              </button>
            </div>
          </div>

          {/* Preview / manual edit pane */}
          <div className="space-y-4">
            <QuoteFieldsPanel state={state} setProp={setProp} setAgent={setAgent} setOption={setOption}
              showAgentProfileSave={isClientAgent}
              onSaveAgentProfile={saveAgentProfile}
              savingAgentProfile={savingProfile} />

            {previewUrl ? (
              <div className="byrd-card p-2">
                <div className="flex items-center justify-between mb-2 px-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Live preview</div>
                  <div className="flex gap-2">
                    <button onClick={refreshPreview} className="text-[11px] text-[#C89434] font-mono">Refresh</button>
                    {editingId && state.listing_agent?.email && (
                      <button onClick={() => emailQuote(editingId, state.listing_agent)}
                        className="byrd-btn byrd-btn-outline text-xs" data-testid="lq-email">
                        <Mail size={12} /> Email
                      </button>
                    )}
                    <button onClick={generateAndSave} disabled={!canGenerate || generating}
                      className="byrd-btn byrd-btn-dark text-xs" data-testid="lq-save">
                      {generating ? "Saving…" : (<><Save size={12} /> Save &amp; Download</>)}
                    </button>
                  </div>
                </div>
                <iframe src={previewUrl} title="Preview" className="w-full h-[520px] border border-[#E4DFD1] rounded" />
                {editingId && (
                  <div className="mt-3 px-2">
                    <QuoteSharePanel quoteId={editingId} />
                  </div>
                )}
              </div>
            ) : (
              <div className="byrd-card p-10 text-center">
                <div className="w-14 h-14 mx-auto rounded-full bg-[#F3EEE0] grid place-items-center text-[#C89434]">
                  <FileText size={22} />
                </div>
                <h3 className="font-serif text-xl font-bold mt-4">Preview will appear here</h3>
                <p className="text-[#6B6558] mt-2 text-sm max-w-sm mx-auto">
                  Once Ada has property + rate options, the PDF preview will populate here. Or click&nbsp;
                  <b>Propose</b> to trigger live rate research.
                </p>
                {canPropose && !state.options.length && (
                  <button onClick={proposeOptions} disabled={proposing}
                    className="byrd-btn byrd-btn-dark mt-4" data-testid="lq-propose-empty">
                    {proposing ? "Researching…" : (<><Wand2 size={14} /> Research rates &amp; propose</>)}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Library */}
        <div className="byrd-card p-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Marketing Library</div>
              <h2 className="font-serif text-2xl font-bold">Saved Loan Quotes</h2>
            </div>
            <div className="text-xs text-[#6B6558]">{library.length} saved</div>
          </div>
          {library.length === 0 ? (
            <div className="text-sm text-[#6B6558]">No saved quotes yet. Build one above and click <b>Save &amp; Download</b>.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="lq-library">
                <thead>
                  <tr className="text-left border-b border-[#E4DFD1] text-[10px] uppercase tracking-widest text-[#6B6558]">
                    <th className="p-2">Property</th>
                    <th className="p-2">Listing Agent</th>
                    <th className="p-2">Created By</th>
                    <th className="p-2">Value</th>
                    <th className="p-2">Created</th>
                    <th className="p-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {library.map((q) => (
                    <tr key={q.id} className="border-b border-[#F3EEE0]">
                      <td className="p-2">
                        <div className="font-semibold">{q.property_info?.name || "—"}</div>
                        <div className="text-[11px] text-[#6B6558]">
                          {q.property_info?.property_type || ""} · {[q.property_info?.city, q.property_info?.state].filter(Boolean).join(", ")}
                        </div>
                      </td>
                      <td className="p-2">
                        <div>{q.listing_agent?.name || "—"}</div>
                        <div className="text-[11px] text-[#6B6558]">{q.listing_agent?.email || ""}</div>
                      </td>
                      <td className="p-2" data-testid={`lq-createdby-${q.id}`}>
                        <div className="text-xs">{q.created_by_name || "Byrd & CO"}</div>
                        <span className={`byrd-chip text-[9px] mt-0.5 ${q.created_by_role === "client" ? "byrd-chip-gold" : ""}`}>
                          {q.created_by_role === "client" ? "AGENT" : "ADMIN"}
                        </span>
                      </td>
                      <td className="p-2">{fmtMoney(q.property_info?.estimated_value)}</td>
                      <td className="p-2 text-[11px] text-[#6B6558]">{new Date(q.created_at).toLocaleString()}</td>
                      <td className="p-2 text-right">
                        <button
                          onClick={() => emailQuote(q.id, q.listing_agent)}
                          disabled={!q.listing_agent?.email}
                          title={q.listing_agent?.email ? `Email to ${q.listing_agent.email}` : "Add listing agent email first"}
                          className="byrd-btn byrd-btn-outline text-xs mr-2 disabled:opacity-40" data-testid={`lq-email-${q.id}`}>
                          <Mail size={12} /> Email
                        </button>
                        <button
                          onClick={() => editQuote(q.id)}
                          className="byrd-btn byrd-btn-outline text-xs mr-2" data-testid={`lq-edit-${q.id}`}>
                          <Edit3 size={12} /> Edit
                        </button>
                        <button
                          onClick={() => openAuthedPdf(`/admin/marketing/quotes/${q.id}/pdf`, q.filename || "loan-quote.pdf")}
                          className="byrd-btn byrd-btn-outline text-xs" data-testid={`lq-dl-${q.id}`}>
                          <Download size={12} /> PDF
                        </button>
                        <QuoteShareButton quote={q} onChanged={loadLibrary} />
                        <button onClick={async () => {
                          if (!window.confirm("Delete this saved quote?")) return;
                          await api.delete(`/admin/marketing/quotes/${q.id}`);
                          loadLibrary();
                        }} className="byrd-btn byrd-btn-outline text-xs ml-2 text-[#8A1F1A] border-[#E38380]" data-testid={`lq-del-${q.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QuoteFieldsPanel({ state, setProp, setAgent, setOption,
                            showAgentProfileSave = false, onSaveAgentProfile,
                            savingAgentProfile = false }) {
  const p = state.property_info || {};
  const a = state.listing_agent || {};
  return (
    <div className="byrd-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Home size={14} className="text-[#C89434]" />
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Property</div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <Inp label="Property Name" value={p.name} onChange={(v) => setProp("name", v)} />
        <Sel label="Property Type" value={p.property_type} onChange={(v) => setProp("property_type", v)}>
          <option value="">Select…</option>
          {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </Sel>
        <Inp label="Address" value={p.address} onChange={(v) => setProp("address", v)} />
        <div className="grid grid-cols-2 gap-2">
          <Inp label="City" value={p.city} onChange={(v) => setProp("city", v)} />
          <Inp label="State" value={p.state} onChange={(v) => setProp("state", v)} />
        </div>
        <Sel label="Occupancy Type" value={p.occupancy_type || ""} onChange={(v) => setProp("occupancy_type", v || null)}>
          <option value="">Select…</option>
          <option value="owner_occupied">Owner-Occupied</option>
          <option value="non_owner_occupied">Non-Owner-Occupied</option>
        </Sel>
        <NumInp label="Est. Value ($)" value={p.estimated_value} onChange={(v) => setProp("estimated_value", v)} />
        <NumInp label="NOI ($)" value={p.noi} onChange={(v) => setProp("noi", v)} />
        <NumInp label="Cap Rate (%)" step="0.01" value={p.cap_rate_pct} onChange={(v) => setProp("cap_rate_pct", v)} />
      </div>

      <div className="flex items-center gap-2 mt-4 mb-2">
        <User size={14} className="text-[#C89434]" />
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Listing Agent</div>
        {showAgentProfileSave && (
          <button
            type="button"
            onClick={onSaveAgentProfile}
            disabled={savingAgentProfile}
            className="ml-auto text-[10px] font-mono uppercase tracking-widest text-[#C89434] hover:text-[#8A6821] flex items-center gap-1 disabled:opacity-40"
            data-testid="save-agent-profile"
            title="Save name, email, phone, brokerage, and photo so it carries to every new quote"
          >
            {savingAgentProfile ? (<><Loader2 size={11} className="animate-spin" /> Saving…</>) : (<><IdCard size={11} /> Save my agent profile</>)}
          </button>
        )}
      </div>
      <div className="flex gap-3 items-start">
        <AgentPhotoBox
          photoB64={a.photo_b64}
          contentType={a.photo_content_type}
          onSet={(b64, ct) => { setAgent("photo_b64", b64); setAgent("photo_content_type", ct); }}
        />
        <div className="grid grid-cols-2 gap-2 text-sm flex-1">
          <Inp label="Name" value={a.name} onChange={(v) => setAgent("name", v)} />
          <Inp label="Email" value={a.email} onChange={(v) => setAgent("email", v)} />
          <Inp label="Phone" value={a.phone} onChange={(v) => setAgent("phone", v)} />
          <Inp label="Brokerage" value={a.brokerage} onChange={(v) => setAgent("brokerage", v)} />
        </div>
      </div>

      {state.options.length > 0 && (
        <>
          <div className="flex items-center gap-2 mt-4 mb-2">
            <Sparkles size={14} className="text-[#C89434]" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Financing Options — edit any cell</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-[#6B6558]">
                  <th></th>
                  {state.options.map((o, i) => (
                    <th key={i} className="p-1 text-center">
                      <input value={o.label || ""} onChange={(e) => setOption(i, "label", e.target.value)}
                        className="w-full text-center bg-transparent border-b border-[#E4DFD1] focus:border-[#C89434] focus:outline-none font-serif font-bold text-sm text-[#C89434]" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { key: "ltv_pct", label: "LTV (%)", type: "num", step: "1" },
                  { key: "loan_amount", label: "Loan Amount", type: "num" },
                  { key: "loan_program", label: "Program", type: "text" },
                  { key: "interest_rate_pct", label: "Rate (%)", type: "num", step: "0.01" },
                  { key: "monthly_payment", label: "Monthly P&I", type: "num" },
                  { key: "recourse", label: "Recourse", type: "select", options: ["Yes", "No", "Partial"] },
                ].map((row) => (
                  <tr key={row.key} className="border-b border-[#F3EEE0]">
                    <td className="p-1 text-[#6B6558]">{row.label}</td>
                    {state.options.map((o, i) => (
                      <td key={i} className="p-1">
                        {row.type === "select" ? (
                          <select value={o[row.key] || ""} onChange={(e) => setOption(i, row.key, e.target.value || null)}
                            className="w-full px-1 py-0.5 bg-transparent border-b border-[#E4DFD1] focus:border-[#C89434] focus:outline-none">
                            <option value="">—</option>
                            {row.options.map((op) => <option key={op} value={op}>{op}</option>)}
                          </select>
                        ) : (
                          <input
                            type={row.type === "num" ? "number" : "text"}
                            step={row.step}
                            value={o[row.key] == null ? "" : o[row.key]}
                            onChange={(e) => setOption(i, row.key, row.type === "num" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
                            className="w-full px-1 py-0.5 bg-transparent border-b border-[#E4DFD1] focus:border-[#C89434] focus:outline-none text-right"
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {state.research_citations?.length > 0 && (
            <details className="mt-3 text-[11px] text-[#6B6558]">
              <summary className="cursor-pointer font-mono uppercase tracking-widest">// Sources from Perplexity</summary>
              <ul className="mt-2 space-y-1">
                {state.research_citations.map((u, i) => (
                  <li key={i}><a href={u} target="_blank" rel="noopener noreferrer" className="underline hover:text-[#C89434] break-all">{u}</a></li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}

function AgentPhotoBox({ photoB64, contentType, onSet }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    const name = (file.name || "").toLowerCase();
    const isImageExt = /\.(jpe?g|png|gif|webp|avif|heic|heif|bmp|tiff?)$/i.test(name);
    const isImageMime = (file.type || "").startsWith("image/");
    // Some OSes hand back an empty MIME type for iPhone HEIC/HEIF exports —
    // fall back to the file extension so those still upload cleanly.
    if (!isImageMime && !isImageExt) {
      toast.error("Please choose an image file (jpg, png, heic, webp).");
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Photo must be under 3 MB.");
      return;
    }
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => {
          const s = r.result || "";
          const c = s.indexOf(",");
          res(c >= 0 ? s.slice(c + 1) : s);
        };
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      // Prefer the real MIME; if the OS didn't give us one, infer from extension.
      let ct = file.type;
      if (!ct) {
        if (/\.heic$/i.test(name)) ct = "image/heic";
        else if (/\.heif$/i.test(name)) ct = "image/heif";
        else if (/\.png$/i.test(name)) ct = "image/png";
        else if (/\.webp$/i.test(name)) ct = "image/webp";
        else ct = "image/jpeg";
      }
      onSet(b64, ct);
    } finally {
      setUploading(false);
    }
  };

  const src = photoB64 ? `data:${contentType || "image/jpeg"};base64,${photoB64}` : null;

  return (
    <div>
      <span className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558]">Photo</span>
      <div className="mt-0.5 w-24 h-32 rounded-md border border-dashed border-[#E4DFD1] overflow-hidden relative bg-[#FBF8F1]">
        {src ? (
          <>
            <img src={src} alt="Agent" className="w-full h-full object-cover" />
            <button
              onClick={() => onSet(null, null)}
              className="absolute top-1 right-1 bg-white/90 hover:bg-white text-[#8A1F1A] rounded p-1"
              title="Remove photo" data-testid="agent-photo-remove"
            >
              <X size={11} />
            </button>
          </>
        ) : (
          <label className="w-full h-full grid place-items-center cursor-pointer hover:bg-[#F3EEE0] transition">
            <div className="text-center text-[#6B6558] text-[10px]">
              {uploading ? <Loader2 size={16} className="animate-spin mx-auto" /> : (<><Upload size={16} className="mx-auto mb-1" /> Upload</>)}
            </div>
            <input type="file"
              accept="image/*,.heic,.heif,.HEIC,.HEIF,.avif,.webp,.jpg,.jpeg,.png"
              className="hidden"
              onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }}
              data-testid="agent-photo-input" />
          </label>
        )}
      </div>
    </div>
  );
}

function Inp({ label, value, onChange }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558]">{label}</span>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-0.5 w-full px-2 py-1.5 border border-[#E4DFD1] rounded text-sm focus:outline-none focus:border-[#C89434]" />
    </label>
  );
}

function NumInp({ label, value, onChange, step }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558]">{label}</span>
      <input type="number" step={step || "1"} value={value == null ? "" : value}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        className="mt-0.5 w-full px-2 py-1.5 border border-[#E4DFD1] rounded text-sm focus:outline-none focus:border-[#C89434]" />
    </label>
  );
}

function Sel({ label, value, onChange, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558]">{label}</span>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="mt-0.5 w-full px-2 py-1.5 border border-[#E4DFD1] rounded text-sm focus:outline-none focus:border-[#C89434]">
        {children}
      </select>
    </label>
  );
}


// --------- Quote Share Link controls ---------

async function copyToClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback for older browsers / non-secure contexts
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    toast.success("Link copied");
    return true;
  } catch (e) {
    toast.error("Couldn't copy — please copy manually");
    return false;
  }
}

/**
 * Compact "Share" button that opens an inline drawer with the share toggle,
 * universal link, per-agent link generator, and view stats. Rendered on each
 * row in the Saved Loan Quotes library.
 */
function QuoteShareButton({ quote, onChanged }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="byrd-btn byrd-btn-outline text-xs ml-2"
        data-testid={`lq-share-${quote.id}`}
        title="Public share link"
      >
        <Link2 size={12} /> Share
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4"
             onClick={() => setOpen(false)} data-testid={`lq-share-modal-${quote.id}`}>
          <div className="byrd-card p-6 max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#6B6558]">// Public Share Link</div>
                <h3 className="font-serif text-xl font-bold">{quote.property_info?.name || "Loan Quote"}</h3>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#6B6558] hover:text-[#1A1A1A]" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <QuoteSharePanel quoteId={quote.id} onChanged={onChanged} />
          </div>
        </div>
      )}
    </>
  );
}

/**
 * The share-link panel itself. Reused in the modal (from the library row) AND
 * inline under the live-preview iframe when editing a saved quote.
 *
 * Contract:
 *   - GET /api/admin/marketing/quotes/{qid}/share/stats — current toggle + slug
 *   - PATCH /api/admin/marketing/quotes/{qid}/share    — toggle enabled
 */
function QuoteSharePanel({ quoteId, onChanged }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [agentTag, setAgentTag] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/marketing/quotes/${quoteId}/share/stats`);
      setStats(r.data);
    } catch (e) {
      toast.error(errMsg(e, "Couldn't load share status"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [quoteId]);

  const toggle = async (enabled) => {
    setToggling(true);
    try {
      await api.patch(`/admin/marketing/quotes/${quoteId}/share`, { enabled });
      toast.success(enabled ? "Public link enabled" : "Public link disabled");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(errMsg(e, "Couldn't update share"));
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return <div className="text-xs text-[#6B6558] font-mono">Loading…</div>;
  }
  if (!stats) return null;

  const enabled = !!stats.enabled;
  const shareUrl = stats.share_url || (stats.slug ? `${window.location.origin}/q/${stats.slug}` : "");
  const perAgentUrl = shareUrl && agentTag.trim()
    ? `${shareUrl}?a=${encodeURIComponent(agentTag.trim())}`
    : "";

  return (
    <div className="space-y-4" data-testid={`lq-share-panel-${quoteId}`}>
      {/* Enable toggle */}
      <div className="flex items-center justify-between rounded-md border border-[#E4DFD1] bg-white p-3">
        <div>
          <div className="font-semibold text-sm">Public Share Link</div>
          <div className="text-[11px] text-[#6B6558]">
            When enabled, anyone with the link sees a branded landing page for this quote.
          </div>
        </div>
        <button
          onClick={() => toggle(!enabled)}
          disabled={toggling}
          className={`relative inline-flex h-6 w-11 rounded-full transition ${enabled ? "bg-[#C89434]" : "bg-[#E4DFD1]"}`}
          data-testid={`lq-share-toggle-${quoteId}`}
          aria-pressed={enabled}
        >
          <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${enabled ? "translate-x-5" : ""}`} />
        </button>
      </div>

      {enabled && shareUrl && (
        <>
          {/* Universal link */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558] mb-1">Universal Link</div>
            <div className="flex gap-2">
              <input
                readOnly
                value={shareUrl}
                onFocus={(e) => e.target.select()}
                className="flex-1 h-10 px-3 rounded-md border border-[#E4DFD1] bg-[#F9F6EE] text-xs font-mono"
                data-testid={`lq-share-url-${quoteId}`}
              />
              <button
                onClick={() => copyToClipboard(shareUrl)}
                className="byrd-btn byrd-btn-dark text-xs"
                data-testid={`lq-share-copy-${quoteId}`}
              >
                <Copy size={12} /> Copy
              </button>
            </div>
            <div className="text-[11px] text-[#6B6558] mt-1">
              Same URL for every visitor. Untagged views bucket under <b>(direct)</b> in stats.
            </div>
          </div>

          {/* Per-agent tracked link */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558] mb-1">Per-Agent Tracked Link</div>
            <div className="flex gap-2">
              <input
                value={agentTag}
                onChange={(e) => setAgentTag(e.target.value)}
                placeholder="Agent name or ID (e.g., jsmith)"
                className="flex-1 h-10 px-3 rounded-md border border-[#E4DFD1] bg-white text-sm"
                data-testid={`lq-share-agent-${quoteId}`}
              />
              <button
                onClick={() => perAgentUrl && copyToClipboard(perAgentUrl)}
                disabled={!perAgentUrl}
                className="byrd-btn byrd-btn-outline text-xs disabled:opacity-40"
                data-testid={`lq-share-agent-copy-${quoteId}`}
              >
                <Copy size={12} /> Copy
              </button>
            </div>
            {perAgentUrl && (
              <div className="text-[11px] text-[#6B6558] mt-1 font-mono break-all">{perAgentUrl}</div>
            )}
            <div className="text-[11px] text-[#6B6558] mt-1">
              Send unique variants to each listing agent — the <code>?a=</code> tag tracks who's driving views.
            </div>
          </div>

          {/* Stats */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#6B6558] mb-1 flex items-center gap-1">
              <BarChart3 size={11} /> Traffic
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-md border border-[#E4DFD1] bg-white p-3 text-center">
                <div className="text-2xl font-bold" data-testid={`lq-share-views-${quoteId}`}>{stats.total_views}</div>
                <div className="text-[11px] text-[#6B6558] font-mono uppercase tracking-widest">Total views</div>
              </div>
              <div className="rounded-md border border-[#E4DFD1] bg-white p-3 text-center">
                <div className="text-2xl font-bold" data-testid={`lq-share-leads-${quoteId}`}>{stats.total_leads}</div>
                <div className="text-[11px] text-[#6B6558] font-mono uppercase tracking-widest">Leads captured</div>
              </div>
            </div>
            {stats.by_agent?.length > 0 && (
              <div className="mt-3 rounded-md border border-[#E4DFD1] bg-white overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9F6EE]">
                    <tr className="text-left text-[10px] uppercase tracking-widest text-[#6B6558]">
                      <th className="p-2">Referring Agent</th>
                      <th className="p-2 text-right">Views</th>
                      <th className="p-2 text-right">Last</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.by_agent.map((row, i) => (
                      <tr key={i} className="border-t border-[#F3EEE0]">
                        <td className="p-2 font-mono">{row.agent}</td>
                        <td className="p-2 text-right">{row.count}</td>
                        <td className="p-2 text-right text-[11px] text-[#6B6558]">
                          {row.last_viewed_at ? new Date(row.last_viewed_at).toLocaleDateString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button
              onClick={load}
              className="text-[11px] text-[#C89434] font-mono mt-2 inline-flex items-center gap-1"
              data-testid={`lq-share-refresh-${quoteId}`}
            >
              <RefreshCw size={11} /> Refresh
            </button>
          </div>
        </>
      )}

      {!enabled && (
        <div className="rounded-md border border-dashed border-[#E4DFD1] bg-[#F9F6EE] p-4 text-sm text-[#6B6558]">
          Turn on <b>Public Share Link</b> above to generate a URL you can send to listing agents.
          The landing page shows property details, indicative loan terms, and a Download PDF button.
        </div>
      )}
    </div>
  );
}
